/**
 * MapRepairModal — UI dla Map Repair Engine v1.
 *
 * Flow:
 *  1. Open → auto-run dry-run repair (nie dotyka DB, wypełnia state propozycjami)
 *  2. User widzi auto-applied summary + review queue (R3/R6) z checkboxami + R5 flagged
 *  3. Klik "Apply" → repair(dryRun=false) z acceptedR3/R6 → re-audit → toast delta
 *  4. Klik "Rollback" (jeśli available) → AlertDialog confirm → rollback mutation
 */

import { useState, useMemo, useEffect, Component, type ErrorInfo, type ReactNode } from "react";
import { Wrench, RotateCcw, Play, Check, AlertTriangle, Loader2, Info, Download } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { useMapRepair } from "@/hooks/useMapRepair";
import { repairLog } from "@/services/contentEngine/contentLogger";
import type { RepairReport, MoveProposal, RelationProposal } from "@/types/mapRepair";
import { formatDistanceToNow } from "date-fns";
import { pl } from "date-fns/locale";

interface MapRepairModalProps {
  mapId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type LogType = "info" | "success" | "error" | "stage";

export function MapRepairModal({ mapId, open, onOpenChange }: MapRepairModalProps) {
  const { repair, rollback, repairRow, rollbackPreview } = useMapRepair(mapId);
  const { toast } = useToast();

  const [logs, setLogs] = useState<Array<{ type: LogType; msg: string }>>([]);
  const [dryRunReport, setDryRunReport] = useState<RepairReport | null>(null);
  const [appliedReport, setAppliedReport] = useState<RepairReport | null>(null);
  const [acceptedR3, setAcceptedR3] = useState<Set<string>>(new Set());
  const [acceptedR6, setAcceptedR6] = useState<Set<string>>(new Set());
  const [showRollbackConfirm, setShowRollbackConfirm] = useState(false);
  const [hasRunDryRun, setHasRunDryRun] = useState(false);

  const addLog = (type: LogType, msg: string) => {
    setLogs((prev) => [...prev.slice(-200), { type, msg }]);
    // Dual-write do globalnego AppLogPanel (kategoria 'map-repair')
    if (type === "error") repairLog.error("MapRepair", msg);
    else repairLog.public("MapRepair", `[${type}] ${msg}`);
  };

  // Auto-odpal dry-run po otwarciu modala (jedyny raz)
  useEffect(() => {
    if (open && !hasRunDryRun && !repair.isPending) {
      setHasRunDryRun(true);
      setLogs([]);
      setDryRunReport(null);
      setAppliedReport(null);
      setAcceptedR3(new Set());
      setAcceptedR6(new Set());
      void (async () => {
        try {
          const res = await repair.mutateAsync({
            dryRun: true,
            onLog: (type, msg) => addLog(type, msg),
          });
          if (res.skipped_by_lock) {
            addLog("error", "Inny audit/repair już trwa — zamknij modal i spróbuj za chwilę");
            return;
          }
          if (res.report) {
            setDryRunReport(res.report);
          }
        } catch (err) {
          addLog("error", (err as Error).message);
        }
      })();
    }
    if (!open) {
      setHasRunDryRun(false);
    }
  }, [open, hasRunDryRun, repair]);

  const r3Proposals = useMemo<MoveProposal[]>(
    () => dryRunReport?.review_queue.R3_proposed_moves ?? [],
    [dryRunReport],
  );
  const r6Proposals = useMemo<RelationProposal[]>(
    () => dryRunReport?.review_queue.R6_proposed_relations ?? [],
    [dryRunReport],
  );

  const counts = useMemo(() => {
    if (!dryRunReport) return null;
    return {
      r1: dryRunReport.auto_applied.R1_same_seed_merges.length,
      r2: dryRunReport.auto_applied.R2_cross_seed_merges.length,
      r4: dryRunReport.auto_applied.R4_relations_cleaned.deleted_count,
      r7: dryRunReport.auto_applied.R7_seeds_demoted.length,
      r3: r3Proposals.length,
      r6: r6Proposals.length,
      r5: dryRunReport.flagged.R5_suspicious_slugs.length,
      skipped: dryRunReport.skipped.has_wp_post.length,
    };
  }, [dryRunReport, r3Proposals, r6Proposals]);

  const handleApply = async () => {
    try {
      const res = await repair.mutateAsync({
        dryRun: false,
        acceptedR3: Array.from(acceptedR3),
        acceptedR6: Array.from(acceptedR6),
        onLog: (type, msg) => addLog(type, msg),
      });
      if (res.report) {
        setAppliedReport(res.report);
        const before = res.report.re_audit_delta.score_before ?? 0;
        const after = res.report.re_audit_delta.score_after ?? 0;
        const delta = after - before;
        toast({
          title: "Repair zastosowany",
          description: `Score ${before}→${after} (${delta >= 0 ? "+" : ""}${delta}) · ${res.report.re_audit_delta.duplicates_resolved} dupów rozwiązanych`,
        });
      }
    } catch (err) {
      toast({
        title: "Repair failed",
        description: (err as Error).message,
        variant: "destructive",
      });
    }
  };

  const triggerDownload = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const buildFilename = (ext: string) => {
    const report = appliedReport || dryRunReport;
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, "0");
    const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
    const dryRunTag = report?.dry_run ? "-DRYRUN" : "";
    return `repair-${mapId.slice(0, 8)}-${stamp}${dryRunTag}.${ext}`;
  };

  const handleDownloadReport = () => {
    const report = appliedReport || dryRunReport;
    if (!report) return;
    triggerDownload(
      JSON.stringify(report, null, 2),
      buildFilename("json"),
      "application/json",
    );
  };

  const handleDownloadMarkdown = () => {
    const report = appliedReport || dryRunReport;
    if (!report) return;
    triggerDownload(
      formatReportAsMarkdown(report, logs),
      buildFilename("md"),
      "text/markdown",
    );
  };

  const handleDownloadTxt = () => {
    const report = appliedReport || dryRunReport;
    if (!report) return;
    triggerDownload(
      formatReportAsTxt(report, logs),
      buildFilename("txt"),
      "text/plain",
    );
  };

  const handleRollback = async () => {
    setShowRollbackConfirm(false);
    try {
      const res = await rollback.mutateAsync({
        onLog: (type, msg) => addLog(type, msg),
      });
      toast({
        title: "Rollback zakończony",
        description: `Przywrócono ${res.reverted.R1_R2_nodes_restored} nodów, ${res.reverted.R1_R2_relations_rewired_back + res.reverted.R4_relations_restored} relacji`,
      });
    } catch (err) {
      toast({
        title: "Rollback failed",
        description: (err as Error).message,
        variant: "destructive",
      });
    }
  };

  const preview = rollbackPreview.data;
  const rollbackAvailable = preview?.available ?? false;

  const toggleAllR3 = (checked: boolean) => {
    if (checked) {
      setAcceptedR3(new Set(r3Proposals.map((p) => p.node_id)));
    } else {
      setAcceptedR3(new Set());
    }
  };
  const acceptR3HighConfidence = () => {
    setAcceptedR3(
      new Set(r3Proposals.filter((p) => p.confidence >= 0.7).map((p) => p.node_id)),
    );
  };

  const toggleAllR6 = (checked: boolean) => {
    if (checked) {
      setAcceptedR6(
        new Set(r6Proposals.map((p) => `${p.from_node_id}::${p.to_node_id}`)),
      );
    } else {
      setAcceptedR6(new Set());
    }
  };
  const acceptR6HighConfidence = () => {
    setAcceptedR6(
      new Set(
        r6Proposals
          .filter((p) => p.confidence >= 0.7)
          .map((p) => `${p.from_node_id}::${p.to_node_id}`),
      ),
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5 text-emerald-600" />
              Napraw mapę — deterministic rules R1-R7
            </DialogTitle>
            <DialogDescription>
              Auto-merge duplikatów (R1/R2) · cleanup broken relations (R4) · demote ghost seeds (R7) ·
              propozycje move nodów (R3) i linkowania orphanów (R6) do akceptacji · flag podejrzanych slug (R5).
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 pr-1">
            {/* Progress / logs */}
            {repair.isPending && (
              <div className="rounded-md border bg-muted/30 p-3 space-y-1 max-h-40 overflow-y-auto font-mono text-xs">
                {logs.slice(-20).map((log, i) => (
                  <div
                    key={i}
                    className={
                      log.type === "error"
                        ? "text-red-600"
                        : log.type === "success"
                        ? "text-emerald-600"
                        : log.type === "stage"
                        ? "text-violet-600 font-semibold"
                        : "text-muted-foreground"
                    }
                  >
                    {log.msg}
                  </div>
                ))}
                <div className="flex items-center gap-2 text-violet-600 pt-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> Przetwarzanie...
                </div>
              </div>
            )}

            {/* Summary kafelki — ukryte po apply (AppliedResults ma własne delta tiles) */}
            {!appliedReport && counts && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <SummaryTile label="R1 same-seed merges" value={counts.r1} color="emerald" />
                <SummaryTile label="R2 cross-seed merges" value={counts.r2} color="emerald" />
                <SummaryTile label="R4 relations cleaned" value={counts.r4} color="emerald" />
                <SummaryTile label="R7 ghost seeds demoted" value={counts.r7} color="emerald" />
                <SummaryTile label="R3 review queue (moves)" value={counts.r3} color="amber" />
                <SummaryTile label="R6 review queue (links)" value={counts.r6} color="amber" />
                <SummaryTile label="R5 flagged slugs" value={counts.r5} color="sky" />
                <SummaryTile
                  label="Skipped (has WP post)"
                  value={counts.skipped}
                  color={counts.skipped > 0 ? "amber" : "muted"}
                />
              </div>
            )}

            {/* PRE-APPLY: Instrukcja flow — pokazywane zanim user kliknie Apply */}
            {!appliedReport && dryRunReport && counts && (
              <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm space-y-1">
                <p className="font-semibold text-emerald-700 dark:text-emerald-400">
                  Co się stanie po kliknięciu "Apply & Re-audit"?
                </p>
                <ul className="text-xs text-muted-foreground space-y-0.5 ml-4 list-disc">
                  <li>
                    <strong className="text-emerald-700 dark:text-emerald-400">
                      Automatycznie (zielone kafelki):
                    </strong>{" "}
                    {counts.r1} same-seed merges + {counts.r2} cross-seed merges + {counts.r4}{" "}
                    broken relations + {counts.r7} ghost seeds demoted ={" "}
                    <strong>{counts.r1 + counts.r2 + counts.r4 + counts.r7} operacji</strong>.
                    Nie musisz nic zaznaczać.
                  </li>
                  <li>
                    <strong className="text-amber-700 dark:text-amber-400">Do Twojej decyzji (amber):</strong>{" "}
                    {counts.r3} proposed moves + {counts.r6} proposed links. Zaznacz checkboxy
                    w tabach poniżej żeby zastosować. Ignorujesz = nie wykonują się. Kliknij{" "}
                    <em>"Tylko confidence ≥ 0.7"</em> żeby automatycznie zaznaczyć bezpieczne.
                  </li>
                  <li>
                    <strong className="text-sky-700 dark:text-sky-400">Tylko flagi (R5):</strong>{" "}
                    {counts.r5} slug do Twojego wglądu — <strong>nic nie jest zmieniane w DB</strong>.
                  </li>
                </ul>
                <p className="text-xs text-muted-foreground pt-1">
                  💡 Najbezpieczniej: kliknij <strong>Apply</strong> bez zaznaczania — wykona się{" "}
                  {counts.r1 + counts.r2 + counts.r4 + counts.r7} auto-fix, review queue omijasz.
                  Orphany z R6 zlinkujesz później przy kolejnym repair run.
                </p>
              </div>
            )}

            {/* POST-APPLY: Wyniki — co zostało zrobione + before/after */}
            {appliedReport && (
              <RepairResultsErrorBoundary onDownload={handleDownloadReport}>
                <AppliedResults
                  report={appliedReport}
                  logs={logs}
                  onDownloadJson={handleDownloadReport}
                  onDownloadMarkdown={handleDownloadMarkdown}
                  onDownloadTxt={handleDownloadTxt}
                />
              </RepairResultsErrorBoundary>
            )}

            {/* Review queue + flagged tabs — ukryte po apply (widok zmienia się na Wyniki) */}
            {!appliedReport && dryRunReport && (counts?.r3 || counts?.r6 || counts?.r5) ? (
              <Tabs defaultValue={counts?.r3 ? "r3" : counts?.r6 ? "r6" : "r5"}>
                <TabsList>
                  {counts?.r3 ? <TabsTrigger value="r3">R3 Moves ({counts.r3})</TabsTrigger> : null}
                  {counts?.r6 ? <TabsTrigger value="r6">R6 Links ({counts.r6})</TabsTrigger> : null}
                  {counts?.r5 ? <TabsTrigger value="r5">R5 Flagged ({counts.r5})</TabsTrigger> : null}
                  {counts?.skipped ? <TabsTrigger value="skip">Skipped ({counts.skipped})</TabsTrigger> : null}
                </TabsList>

                <TabsContent value="r3" className="space-y-2">
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => toggleAllR3(true)}>
                      Zaznacz wszystkie
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => toggleAllR3(false)}>
                      Odznacz
                    </Button>
                    <Button size="sm" variant="outline" onClick={acceptR3HighConfidence}>
                      Tylko confidence ≥ 0.7
                    </Button>
                  </div>
                  <R3Table
                    proposals={r3Proposals}
                    accepted={acceptedR3}
                    onToggle={(id, v) => {
                      const next = new Set(acceptedR3);
                      if (v) next.add(id);
                      else next.delete(id);
                      setAcceptedR3(next);
                    }}
                  />
                </TabsContent>

                <TabsContent value="r6" className="space-y-2">
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => toggleAllR6(true)}>
                      Zaznacz wszystkie
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => toggleAllR6(false)}>
                      Odznacz
                    </Button>
                    <Button size="sm" variant="outline" onClick={acceptR6HighConfidence}>
                      Tylko confidence ≥ 0.7
                    </Button>
                  </div>
                  <R6Table
                    proposals={r6Proposals}
                    accepted={acceptedR6}
                    onToggle={(key, v) => {
                      const next = new Set(acceptedR6);
                      if (v) next.add(key);
                      else next.delete(key);
                      setAcceptedR6(next);
                    }}
                  />
                </TabsContent>

                <TabsContent value="r5" className="space-y-3">
                  {/* Panel wyjaśniający co R5 robi */}
                  <div className="rounded-md border border-sky-500/30 bg-sky-500/5 p-3 text-sm space-y-2">
                    <p className="font-semibold text-sky-700 dark:text-sky-400">
                      Co to jest R5 Flagged?
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Lista slugów które wyglądają podejrzanie — potencjalne literówki, obcięcia
                      LLM, błędna slugifikacja. <strong>R5 nic nie zmienia w DB</strong> —
                      tylko pokazuje Ci podejrzane przypadki do ręcznej weryfikacji. Zmiana slugu
                      na opublikowanym wpisie zepsuje URL, więc TY decydujesz co z tym zrobić.
                    </p>
                    <div className="text-xs space-y-1 pt-1">
                      <p>
                        <Badge variant="outline" className="text-[10px] mr-1">double_hyphen</Badge>
                        dwa myślniki pod rząd (<code>foo--bar</code>) — artefakt normalizacji.
                      </p>
                      <p>
                        <Badge variant="outline" className="text-[10px] mr-1">short_middle_token:X</Badge>
                        krótkie słowo <code>X</code> (≤2 znaki) w środku slugu, które nie jest
                        polskim/angielskim spójnikiem (do, w, na, of...) — sygnał obcięcia przez LLM.
                      </p>
                      <p>
                        <Badge variant="outline" className="text-[10px] mr-1">repeated_token:X</Badge>
                        to samo słowo <code>X</code> pojawia się dwa razy pod rząd (<code>sushi-sushi</code>).
                      </p>
                      <p>
                        <Badge variant="outline" className="text-[10px] mr-1">too_long_Nchars</Badge>
                        slug &gt; 80 znaków — zwykle LLM przepisało pół tytułu do slugu.
                      </p>
                      <p>
                        <Badge variant="outline" className="text-[10px] mr-1">non_ascii</Badge>
                        slug zawiera polskie znaki (ą, ć, ł, ś…) — powinny być slugifikowane.
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground pt-1">
                      <strong>Co zrobić?</strong> Jeśli flagowany slug jest OK (false positive —
                      zwłaszcza polski spójnik którego nie ma na whitelist) — zignoruj. Jeśli faktycznie
                      źle wygląda: <strong>nie zmieniaj slugu dla noda z matched WP post</strong>
                      (zepsujesz URL). Dla nodów bez matcha: edytuj slug ręcznie w NodesTab.
                    </p>
                  </div>

                  <div className="rounded-md border bg-muted/10 divide-y max-h-[400px] overflow-y-auto">
                    {dryRunReport.flagged.R5_suspicious_slugs.map((f, i) => (
                      <div key={i} className="p-2 flex items-start gap-2 text-sm">
                        <AlertTriangle className="h-4 w-4 text-sky-600 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <code className="font-mono text-xs break-all">{f.slug}</code>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {f.reasons.map((r, ri) => (
                              <Badge
                                key={ri}
                                variant="outline"
                                className="text-[10px]"
                                title={humanizeR5Reason(r)}
                              >
                                {r}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="skip">
                  <div className="rounded-md border bg-muted/10 divide-y">
                    {dryRunReport.skipped.has_wp_post.map((s, i) => (
                      <div key={i} className="p-2 text-sm flex items-center gap-2">
                        <Info className="h-4 w-4 text-amber-600 shrink-0" />
                        <code className="text-xs">{s.slug}</code>
                        <span className="text-xs text-muted-foreground">
                          · {s.rule} · {s.reason}
                        </span>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            ) : null}

            {/* Observability: cross_seed_bridges_delta */}
            {dryRunReport?.re_audit_delta.cross_seed_bridges_delta !== null &&
              Math.abs(dryRunReport?.re_audit_delta.cross_seed_bridges_delta ?? 0) > 10 && (
                <div className="rounded-md border border-sky-500/30 bg-sky-500/5 p-3 text-xs">
                  <Info className="h-3.5 w-3.5 inline mr-1 text-sky-600" />
                  <strong>R2 move</strong> spowodował delta cross-seed bridges: {dryRunReport.re_audit_delta.cross_seed_bridges_delta}.
                  Nie bug — artefakt zmiany seed_id. Brak action required.
                </div>
              )}
          </div>

          {/* Footer */}
          <div className="border-t pt-3 mt-2 flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 text-xs mr-auto">
              {appliedReport ? (
                <>
                  <Check className="h-3 w-3 text-emerald-600 shrink-0" />
                  <span className="text-emerald-700 dark:text-emerald-400 font-semibold">
                    Repair zastosowany — {appliedReport.auto_applied.R1_same_seed_merges.length +
                      appliedReport.auto_applied.R2_cross_seed_merges.length +
                      appliedReport.auto_applied.R4_relations_cleaned.deleted_count +
                      appliedReport.auto_applied.R7_seeds_demoted.length}{" "}
                    operacji wykonanych
                  </span>
                </>
              ) : dryRunReport && counts ? (
                <>
                  <Check className="h-3 w-3 text-emerald-600 shrink-0" />
                  <span className="text-emerald-700 dark:text-emerald-400 font-semibold">
                    {counts.r1 + counts.r2 + counts.r4 + counts.r7} auto
                  </span>
                  <span className="text-muted-foreground">+</span>
                  <span className="text-amber-700 dark:text-amber-400 font-semibold">
                    {acceptedR3.size + acceptedR6.size} z review queue
                  </span>
                  <span className="text-muted-foreground">
                    {" "}
                    ={" "}
                    {counts.r1 +
                      counts.r2 +
                      counts.r4 +
                      counts.r7 +
                      acceptedR3.size +
                      acceptedR6.size}{" "}
                    operacji do wykonania
                  </span>
                </>
              ) : repair.isPending ? (
                <span className="text-muted-foreground">Przygotowuję dry-run...</span>
              ) : null}
            </div>

            {rollbackAvailable && preview && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowRollbackConfirm(true)}
                disabled={rollback.isPending}
                className="border-red-500/40 text-red-600 dark:text-red-400 hover:bg-red-500/10"
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                Rollback (dostępny {preview.rollback_available_until ? formatDistanceToNow(new Date(preview.rollback_available_until), { addSuffix: true, locale: pl }) : ""})
              </Button>
            )}

            {appliedReport ? (
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => onOpenChange(false)}
              >
                <Check className="h-3.5 w-3.5 mr-1" />
                Gotowe
              </Button>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
                  Zamknij
                </Button>
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={handleApply}
                  disabled={!dryRunReport || repair.isPending}
                >
                  <Play className="h-3.5 w-3.5 mr-1" />
                  {repair.isPending && dryRunReport ? "Applying..." : "Apply & Re-audit"}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Rollback confirmation */}
      <AlertDialog open={showRollbackConfirm} onOpenChange={setShowRollbackConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Cofnąć repair z {preview?.executed_at ? formatDistanceToNow(new Date(preview.executed_at), { addSuffix: true, locale: pl }) : "?"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {preview && (
                <>
                  Przywróci <strong>{preview.deletedNodes}</strong> usuniętych nodów,
                  <strong> {preview.rewiredRelations + preview.cleanedRelations}</strong> relacji,
                  <strong> {preview.demotedSeeds}</strong> zdemotowanych seedów,
                  <strong> {preview.movedNodes}</strong> przeniesionych nodów oraz
                  usunie <strong>{preview.linkedRelations}</strong> dodanych relacji.
                  Audit zostanie automatycznie przeliczony po rollback.
                  <br />
                  <br />
                  <strong className="text-red-600">Tej operacji nie można cofnąć.</strong>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anuluj</AlertDialogCancel>
            <AlertDialogAction onClick={handleRollback}>
              Potwierdź rollback
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function SummaryTile({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: "emerald" | "amber" | "sky" | "muted";
}) {
  const cls =
    color === "emerald"
      ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400"
      : color === "amber"
      ? "border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-400"
      : color === "sky"
      ? "border-sky-500/30 bg-sky-500/5 text-sky-700 dark:text-sky-400"
      : "border-border bg-muted/30 text-muted-foreground";
  return (
    <div className={`rounded-lg border p-3 ${cls}`}>
      <p className="text-xs uppercase tracking-wider opacity-80">{label}</p>
      <p className="text-2xl font-bold tabular-nums mt-0.5">{value}</p>
    </div>
  );
}

function R3Table({
  proposals,
  accepted,
  onToggle,
}: {
  proposals: MoveProposal[];
  accepted: Set<string>;
  onToggle: (id: string, v: boolean) => void;
}) {
  return (
    <div className="rounded-md border bg-card divide-y max-h-[400px] overflow-y-auto">
      <div className="grid grid-cols-[32px,2fr,1.5fr,1.5fr,100px] gap-2 px-3 py-2 bg-muted/30 text-xs uppercase font-semibold text-muted-foreground">
        <div />
        <div>Node slug</div>
        <div>Current seed</div>
        <div>Proposed seed</div>
        <div className="text-right">Confidence</div>
      </div>
      {proposals.map((p) => {
        const checked = accepted.has(p.node_id);
        return (
          <div
            key={p.node_id}
            className="grid grid-cols-[32px,2fr,1.5fr,1.5fr,100px] gap-2 px-3 py-2 text-sm items-center hover:bg-muted/20"
          >
            <Checkbox
              checked={checked}
              onCheckedChange={(v) => onToggle(p.node_id, !!v)}
            />
            <code className="text-xs font-mono break-all">{p.node_slug}</code>
            <span className="text-xs truncate">{p.current_seed_name} (j={p.current_jaccard})</span>
            <span className="text-xs truncate">{p.proposed_seed_name} (j={p.alt_jaccard})</span>
            <span className="text-right text-xs tabular-nums font-semibold">
              {(p.confidence * 100).toFixed(0)}%
            </span>
          </div>
        );
      })}
      {proposals.length === 0 && (
        <div className="p-4 text-center text-sm text-muted-foreground">Brak propozycji</div>
      )}
    </div>
  );
}

function AppliedResults({
  report,
  logs,
  onDownloadJson,
  onDownloadMarkdown,
  onDownloadTxt,
}: {
  report: RepairReport;
  logs: Array<{ type: LogType; msg: string }>;
  onDownloadJson: () => void;
  onDownloadMarkdown: () => void;
  onDownloadTxt: () => void;
}) {
  // Defensive access — jeśli orchestrator zwróci partial report (np. apply failed w połowie),
  // report może nie mieć wszystkich pól. Wszystko poniżej chronione przez ?? defaults.
  const delta = report.re_audit_delta ?? {
    score_before: null,
    score_after: null,
    l2_det_before: null,
    l2_det_after: null,
    duplicates_resolved: 0,
    orphans_resolved: 0,
    cross_seed_bridges_before: null,
    cross_seed_bridges_after: null,
    cross_seed_bridges_delta: null,
  };
  const scoreDelta =
    delta.score_before != null && delta.score_after != null
      ? delta.score_after - delta.score_before
      : null;
  const autoApplied = report.auto_applied ?? {
    R1_same_seed_merges: [],
    R2_cross_seed_merges: [],
    R4_relations_cleaned: { deleted_count: 0, sample: [] },
    R7_seeds_demoted: [],
  };
  const r1 = autoApplied.R1_same_seed_merges ?? [];
  const r2 = autoApplied.R2_cross_seed_merges ?? [];
  const r4Count = autoApplied.R4_relations_cleaned?.deleted_count ?? 0;
  const r7Seeds = autoApplied.R7_seeds_demoted ?? [];
  const totalAutoOps = r1.length + r2.length + r4Count + r7Seeds.length;
  const executed = report.executed_at ? new Date(report.executed_at) : new Date();
  const durationS = (((report.duration_ms ?? 0) / 1000)).toFixed(1);

  return (
    <div className="space-y-4">
      {/* Header z delta score */}
      <div className="rounded-lg border-2 border-emerald-500/50 bg-emerald-500/5 p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400 flex items-center gap-2">
              <Check className="h-4 w-4" />
              Repair zastosowany · {executed.toLocaleTimeString("pl-PL")} ·{" "}
              {durationS}s
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Wykonano <strong className="text-emerald-700 dark:text-emerald-400">{totalAutoOps}</strong>{" "}
              auto-operacji
              {(report.skipped?.has_wp_post?.length ?? 0) > 0 && (
                <>
                  {" "}· pominięto <strong className="text-amber-700 dark:text-amber-400">{report.skipped.has_wp_post.length}</strong>{" "}
                  nodów (chronione live URL — patrz sekcja "Pominięte" poniżej)
                </>
              )}
              . Re-audit przeliczony automatycznie.
            </p>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <Button variant="outline" size="sm" onClick={onDownloadMarkdown}>
              <Download className="h-3.5 w-3.5 mr-1" />
              .md (human-readable)
            </Button>
            <Button variant="outline" size="sm" onClick={onDownloadTxt}>
              <Download className="h-3.5 w-3.5 mr-1" />
              .txt (log)
            </Button>
            <Button variant="outline" size="sm" onClick={onDownloadJson}>
              <Download className="h-3.5 w-3.5 mr-1" />
              .json (raw)
            </Button>
          </div>
        </div>

        {/* Before / After */}
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
          <DeltaTile
            label="Total score"
            before={delta.score_before}
            after={delta.score_after}
            suffix="/100"
          />
          <DeltaTile
            label="L2 deterministic"
            before={delta.l2_det_before}
            after={delta.l2_det_after}
            suffix="/20"
          />
          <DeltaTile
            label="Cross-seed bridges"
            before={delta.cross_seed_bridges_before}
            after={delta.cross_seed_bridges_after}
            invertColor
          />
          <DeltaTile
            label="Duplikaty rozwiązane"
            before={null}
            after={delta.duplicates_resolved}
            onlyAfter
          />
        </div>

        {scoreDelta !== null && Math.abs(scoreDelta) > 0 && (
          <p className="text-sm mt-2 font-semibold">
            <span className="text-muted-foreground">Zmiana total score: </span>
            <span
              className={
                scoreDelta > 0
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-red-600 dark:text-red-400"
              }
            >
              {scoreDelta > 0 ? "+" : ""}
              {scoreDelta} pkt
            </span>
          </p>
        )}
      </div>

      {/* Tabela Before → After — pełna lista zmian w formacie porównawczym */}
      {(r1.length > 0 || r2.length > 0) && (
        <div className="space-y-2">
          <h4 className="font-semibold text-sm flex items-center gap-2">
            📋 Tabela zmian — co zamieniło na co ({r1.length + r2.length} merges)
          </h4>
          <div className="rounded-lg border bg-card overflow-hidden">
            <div className="overflow-x-auto max-h-80 overflow-y-auto">
              <table className="w-full text-[11px]">
                <thead className="sticky top-0 bg-muted/70 backdrop-blur-sm">
                  <tr className="border-b">
                    <th className="text-left p-2 font-semibold">Reguła</th>
                    <th className="text-left p-2 font-semibold">Entity × Intent</th>
                    <th className="text-left p-2 font-semibold">✅ Canonical (zostaje)</th>
                    <th className="text-left p-2 font-semibold">❌ Usunięte</th>
                    <th className="text-right p-2 font-semibold">Rel. rewired</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ...r1.map((m) => ({ ...m, rule: "R1" as const })),
                    ...r2.map((m) => ({ ...m, rule: "R2" as const })),
                  ].map((m, i) => {
                    const deleted = m?.deleted ?? [];
                    return (
                      <tr key={i} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="p-2 align-top">
                          <Badge variant="outline" className="text-[9px]">
                            {m.rule}
                          </Badge>
                        </td>
                        <td className="p-2 align-top">
                          <span className="font-mono text-[11px]">
                            {m?.entity ?? "?"}
                          </span>
                          <span className="text-muted-foreground text-[10px] block">
                            {m?.intent ?? "?"}
                          </span>
                        </td>
                        <td className="p-2 align-top">
                          <code className="text-emerald-600 dark:text-emerald-400 break-all">
                            {m?.canonical?.slug ?? "?"}
                          </code>
                          {m.rule === "R2" && m.target_seed_id && (
                            <span className="text-[10px] text-muted-foreground block">
                              → seed {m.target_seed_id.slice(0, 8)}
                            </span>
                          )}
                        </td>
                        <td className="p-2 align-top">
                          {deleted.map((d, di) => (
                            <div key={di} className="text-red-600 dark:text-red-400 text-[10px]">
                              <code className="break-all">{d?.slug ?? "?"}</code>
                            </div>
                          ))}
                        </td>
                        <td className="p-2 align-top text-right tabular-nums text-muted-foreground">
                          {m?.relations_rewired ?? 0}
                          {(m?.self_loops_removed ?? 0) > 0 && (
                            <span className="block text-[9px]">
                              -{m.self_loops_removed} loop
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Szczegółowy log operacji */}
      <div className="space-y-2">
        <h4 className="font-semibold text-sm">Co zostało zrobione (zwinięte szczegóły)</h4>

        {r1.length > 0 && (
          <details className="rounded border bg-muted/20" open>
            <summary className="cursor-pointer p-2 text-sm font-medium flex items-center gap-2 hover:bg-muted/40">
              <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 text-[10px] font-bold flex items-center justify-center">
                R1
              </span>
              {r1.length} same-seed merges (
              {r1.reduce((sum, m) => sum + (m?.deleted?.length ?? 0), 0)} nodów usunięto)
            </summary>
            <div className="p-2 space-y-1 max-h-48 overflow-y-auto border-t">
              {r1.map((m, i) => {
                const deleted = m?.deleted ?? [];
                return (
                  <div key={i} className="text-xs p-1.5 rounded bg-background/70">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px]">
                        canonical
                      </Badge>
                      <code className="font-mono">{m?.canonical?.slug ?? "?"}</code>
                      <span className="text-muted-foreground">
                        · {m?.entity ?? "?"} × {m?.intent ?? "?"}
                      </span>
                    </div>
                    <div className="text-muted-foreground mt-0.5 text-[11px]">
                      Usunięto {deleted.length}:{" "}
                      {deleted.map((d) => d?.slug ?? "?").join(", ")}
                    </div>
                    <div className="text-muted-foreground text-[10px]">
                      Relacje przerouted: {m?.relations_rewired ?? 0} · self-loops:{" "}
                      {m?.self_loops_removed ?? 0}
                    </div>
                  </div>
                );
              })}
            </div>
          </details>
        )}

        {r2.length > 0 && (
          <details className="rounded border bg-muted/20" open>
            <summary className="cursor-pointer p-2 text-sm font-medium flex items-center gap-2 hover:bg-muted/40">
              <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 text-[10px] font-bold flex items-center justify-center">
                R2
              </span>
              {r2.length} cross-seed merges (
              {r2.reduce((sum, m) => sum + (m?.deleted?.length ?? 0), 0)} nodów usunięto)
            </summary>
            <div className="p-2 space-y-1 max-h-48 overflow-y-auto border-t">
              {r2.map((m, i) => {
                const deleted = m?.deleted ?? [];
                return (
                  <div key={i} className="text-xs p-1.5 rounded bg-background/70">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px]">
                        canonical
                      </Badge>
                      <code className="font-mono">{m?.canonical?.slug ?? "?"}</code>
                      <span className="text-muted-foreground">
                        · {m?.entity ?? "?"} × {m?.intent ?? "?"}
                      </span>
                      {m?.target_seed_id && (
                        <Badge variant="outline" className="text-[10px]">
                          → seed {m.target_seed_id.slice(0, 8)}
                        </Badge>
                      )}
                    </div>
                    <div className="text-muted-foreground mt-0.5 text-[11px]">
                      Usunięto {deleted.length}:{" "}
                      {deleted.map((d) => d?.slug ?? "?").join(", ")}
                    </div>
                  </div>
                );
              })}
            </div>
          </details>
        )}

        {r4Count > 0 && (
          <details className="rounded border bg-muted/20">
            <summary className="cursor-pointer p-2 text-sm font-medium flex items-center gap-2 hover:bg-muted/40">
              <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 text-[10px] font-bold flex items-center justify-center">
                R4
              </span>
              {r4Count} broken relations usuniętych
            </summary>
            <div className="p-2 text-xs border-t text-muted-foreground">
              Relacje wskazujące na nieistniejące nody zostały usunięte.
            </div>
          </details>
        )}

        {r7Seeds.length > 0 && (
          <details className="rounded border bg-muted/20" open>
            <summary className="cursor-pointer p-2 text-sm font-medium flex items-center gap-2 hover:bg-muted/40">
              <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 text-[10px] font-bold flex items-center justify-center">
                R7
              </span>
              {r7Seeds.length} ghost seeds zdemotowanych do{" "}
              <code className="text-[10px] bg-muted px-1">pending_production</code>
            </summary>
            <div className="p-2 space-y-1 border-t">
              {r7Seeds.map((s, i) => (
                <div key={i} className="text-xs p-1.5 rounded bg-background/70">
                  <strong>{s?.seed_name ?? "?"}</strong>
                  <span className="text-muted-foreground ml-2">
                    · {s?.total_nodes ?? 0} nodów · było: {s?.old_status ?? "?"}
                  </span>
                </div>
              ))}
            </div>
          </details>
        )}

        {totalAutoOps === 0 && (
          <div className="rounded border bg-muted/20 p-3 text-sm text-muted-foreground">
            Żadnych auto-operacji nie wykonano (mapa już była w dobrym stanie).
          </div>
        )}

        {/* Pominięte — chronione live URL */}
        {(report.skipped?.has_wp_post?.length ?? 0) > 0 && (
          <details className="rounded border border-amber-500/30 bg-amber-500/5">
            <summary className="cursor-pointer p-2 text-sm font-medium flex items-center gap-2 hover:bg-amber-500/10">
              <span className="w-5 h-5 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-400 text-[10px] font-bold flex items-center justify-center">
                🛡
              </span>
              <span className="text-amber-700 dark:text-amber-400">
                {report.skipped.has_wp_post.length} nodów POMINIĘTYCH (chronione live URL)
              </span>
            </summary>
            <div className="p-3 border-t space-y-2">
              <p className="text-xs text-muted-foreground leading-relaxed">
                <strong className="text-foreground">Dlaczego te grupy nie zostały zmergowane?</strong>
                <br />
                Każda z tych grup zawiera przynajmniej jeden node który ma
                <code className="mx-1 px-1 bg-muted rounded">matched_wp_post_id</code>
                — czyli jest podpięty do LIVE URL w WordPress. Engine nigdy nie usuwa
                takich nodów bo zepsułoby to URL (404 w Google → spadek rankingu).
                <br />
                <br />
                <strong className="text-foreground">Co z tym zrobić?</strong>
                <br />
                Te grupy nadal będą widoczne w AuditPanel jako "duplicate groups".
                Żeby je zmergować, trzeba <strong>ręcznie na WordPress</strong>:
                (1) wybrać który wpis zostaje (canonical), (2) usunąć pozostałe wpisy
                z odpowiednim redirect 301 do canonical, (3) ponowić repair.
              </p>
              <div className="max-h-48 overflow-y-auto space-y-0.5">
                {report.skipped.has_wp_post.map((s, i) => (
                  <div key={i} className="text-[11px] font-mono flex items-center gap-2">
                    <Badge variant="outline" className="text-[9px] shrink-0">
                      {s?.rule ?? "?"}
                    </Badge>
                    <code className="truncate">{s?.slug ?? "?"}</code>
                  </div>
                ))}
              </div>
            </div>
          </details>
        )}

        {/* Log raw */}
        <details className="rounded border bg-muted/20">
          <summary className="cursor-pointer p-2 text-sm font-medium hover:bg-muted/40">
            Raw log ({logs.length} wpisów)
          </summary>
          <div className="p-2 max-h-64 overflow-y-auto border-t font-mono text-[11px] space-y-0.5">
            {logs.map((log, i) => (
              <div
                key={i}
                className={
                  log.type === "error"
                    ? "text-red-600"
                    : log.type === "success"
                    ? "text-emerald-600"
                    : log.type === "stage"
                    ? "text-violet-600 font-semibold"
                    : "text-muted-foreground"
                }
              >
                {log.msg}
              </div>
            ))}
          </div>
        </details>
      </div>
    </div>
  );
}

function DeltaTile({
  label,
  before,
  after,
  suffix,
  invertColor,
  onlyAfter,
}: {
  label: string;
  before: number | null;
  after: number | null;
  suffix?: string;
  invertColor?: boolean;
  onlyAfter?: boolean;
}) {
  const delta =
    before != null && after != null ? after - before : null;
  const deltaColor =
    delta === null || delta === 0
      ? "text-muted-foreground"
      : (invertColor ? delta < 0 : delta > 0)
      ? "text-emerald-600 dark:text-emerald-400"
      : "text-red-600 dark:text-red-400";

  return (
    <div className="rounded-md border bg-background/60 p-2">
      <p className="text-[10px] uppercase text-muted-foreground tracking-wider">
        {label}
      </p>
      {onlyAfter ? (
        <p className="text-lg font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
          {after ?? "?"}
        </p>
      ) : (
        <div className="flex items-baseline gap-2">
          <span className="text-sm text-muted-foreground tabular-nums">
            {before ?? "?"}
          </span>
          <span className="text-muted-foreground">→</span>
          <span className="text-lg font-bold tabular-nums">
            {after ?? "?"}
            {suffix && (
              <span className="text-xs text-muted-foreground">{suffix}</span>
            )}
          </span>
          {delta !== null && delta !== 0 && (
            <span className={`text-xs font-semibold ${deltaColor}`}>
              ({delta > 0 ? "+" : ""}
              {delta})
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * ErrorBoundary — chroni AppliedResults przed crashem całego modala.
 * Jeśli renderer dostanie partial/malformed report (np. apply failed w połowie),
 * zamiast pustego białego ekranu pokazuje error + button "Pobierz raport JSON" +
 * "Zamknij modal".
 */
class RepairResultsErrorBoundary extends Component<
  { children: ReactNode; onDownload?: () => void },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode; onDownload?: () => void }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[MAP-REPAIR] AppliedResults render error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 text-sm space-y-2">
          <p className="font-semibold text-amber-700 dark:text-amber-400">
            ⚠ Repair zakończony, ale wyniki nie mogą być wyświetlone
          </p>
          <p className="text-xs text-muted-foreground">
            Raport w DB może być niekompletny (np. apply failed w trakcie).
            Pobierz JSON żeby zobaczyć co siedzi w thematic_maps.repair_report.
          </p>
          {this.state.error?.message && (
            <pre className="text-[10px] bg-muted/50 p-2 rounded overflow-x-auto">
              {this.state.error.message}
            </pre>
          )}
          {this.props.onDownload && (
            <Button variant="outline" size="sm" onClick={this.props.onDownload}>
              Pobierz raport JSON (surowe dane)
            </Button>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Human-readable Markdown raport. Struktura:
 *  # Header + meta
 *  ## Delta metrics (before/after)
 *  ## Tabela zmian (R1+R2)
 *  ## Pominięte (chronione live URL)
 *  ## Review queue (R3/R6)
 *  ## Flagged (R5)
 *  ## Raw log
 */
function formatReportAsMarkdown(
  report: RepairReport,
  logs: Array<{ type: string; msg: string }>,
): string {
  const lines: string[] = [];
  const autoApplied = report.auto_applied ?? ({} as RepairReport["auto_applied"]);
  const r1 = autoApplied.R1_same_seed_merges ?? [];
  const r2 = autoApplied.R2_cross_seed_merges ?? [];
  const r4 = autoApplied.R4_relations_cleaned;
  const r7 = autoApplied.R7_seeds_demoted ?? [];
  const delta = report.re_audit_delta ?? ({} as RepairReport["re_audit_delta"]);
  const skipped = report.skipped?.has_wp_post ?? [];
  const r3Proposals = report.review_queue?.R3_proposed_moves ?? [];
  const r6Proposals = report.review_queue?.R6_proposed_relations ?? [];
  const r5Flags = report.flagged?.R5_suspicious_slugs ?? [];

  lines.push(`# Map Repair Report`);
  lines.push(``);
  lines.push(`- **Map ID**: \`${report.map_id}\``);
  lines.push(`- **Executed at**: ${report.executed_at ?? "?"}`);
  lines.push(`- **Duration**: ${((report.duration_ms ?? 0) / 1000).toFixed(1)}s`);
  lines.push(`- **Dry-run**: ${report.dry_run ? "YES" : "NO (applied to DB)"}`);
  lines.push(`- **Repair version**: ${report.repair_version ?? "?"}`);
  lines.push(`- **Rollback available until**: ${report.rollback_available_until ?? "?"}`);
  lines.push(``);

  lines.push(`## Delta Metrics`);
  lines.push(``);
  lines.push(`| Metric | Before | After | Delta |`);
  lines.push(`|---|---|---|---|`);
  lines.push(`| **Total score** | ${delta.score_before ?? "?"} | ${delta.score_after ?? "?"} | ${delta.score_before != null && delta.score_after != null ? (delta.score_after - delta.score_before > 0 ? "+" : "") + (delta.score_after - delta.score_before) : "?"} |`);
  lines.push(`| **L2 deterministic** | ${delta.l2_det_before ?? "?"} | ${delta.l2_det_after ?? "?"} | ${delta.l2_det_before != null && delta.l2_det_after != null ? (delta.l2_det_after - delta.l2_det_before > 0 ? "+" : "") + (delta.l2_det_after - delta.l2_det_before) : "?"} |`);
  lines.push(`| Cross-seed bridges | ${delta.cross_seed_bridges_before ?? "?"} | ${delta.cross_seed_bridges_after ?? "?"} | ${delta.cross_seed_bridges_delta != null ? (delta.cross_seed_bridges_delta > 0 ? "+" : "") + delta.cross_seed_bridges_delta : "?"} |`);
  lines.push(`| **Duplikaty rozwiązane** | — | **${delta.duplicates_resolved ?? 0}** | — |`);
  lines.push(`| Orphans resolved | — | ${delta.orphans_resolved ?? 0} | — |`);
  lines.push(``);

  lines.push(`## Summary`);
  lines.push(``);
  lines.push(`- ✅ **R1 same-seed merges**: ${r1.length}`);
  lines.push(`- ✅ **R2 cross-seed merges**: ${r2.length}`);
  lines.push(`- ✅ **R4 broken relations cleaned**: ${r4?.deleted_count ?? 0}`);
  lines.push(`- ✅ **R7 ghost seeds demoted**: ${r7.length}`);
  lines.push(`- 🛡 **SKIPPED (live URL protected)**: ${skipped.length}`);
  lines.push(`- 📋 R3 proposed moves (review queue): ${r3Proposals.length}`);
  lines.push(`- 📋 R6 proposed links (review queue): ${r6Proposals.length}`);
  lines.push(`- ⚠️ R5 flagged slugs (info only): ${r5Flags.length}`);
  lines.push(``);

  if (r1.length + r2.length > 0) {
    lines.push(`## Tabela zmian — co zamieniło na co`);
    lines.push(``);
    lines.push(`| Rule | Entity | Intent | ✅ Canonical (zostaje) | ❌ Usunięte | Rel. rewired | Self-loops |`);
    lines.push(`|---|---|---|---|---|---|---|`);
    for (const m of r1) {
      const deleted = m?.deleted ?? [];
      lines.push(`| R1 | ${m?.entity ?? "?"} | ${m?.intent ?? "?"} | \`${m?.canonical?.slug ?? "?"}\` | ${deleted.map((d) => `\`${d?.slug ?? "?"}\``).join(", ")} | ${m?.relations_rewired ?? 0} | ${m?.self_loops_removed ?? 0} |`);
    }
    for (const m of r2) {
      const deleted = m?.deleted ?? [];
      lines.push(`| R2 | ${m?.entity ?? "?"} | ${m?.intent ?? "?"} | \`${m?.canonical?.slug ?? "?"}\` (→ seed ${(m?.target_seed_id ?? "?").slice(0, 8)}) | ${deleted.map((d) => `\`${d?.slug ?? "?"}\``).join(", ")} | ${m?.relations_rewired ?? 0} | ${m?.self_loops_removed ?? 0} |`);
    }
    lines.push(``);
  }

  if (r7.length > 0) {
    lines.push(`## Ghost seeds demoted (R7)`);
    lines.push(``);
    for (const s of r7) {
      lines.push(`- **${s?.seed_name ?? "?"}** (${s?.total_nodes ?? 0} nodów, było: ${s?.old_status ?? "?"} → pending_production)`);
    }
    lines.push(``);
  }

  if (skipped.length > 0) {
    lines.push(`## 🛡 Pominięte — chronione live URL (${skipped.length})`);
    lines.push(``);
    lines.push(`> Te nody mają \`matched_wp_post_id\` (live URL w WordPress). Engine NIGDY nie usuwa takich nodów żeby nie zepsuć URL (404 → spadek rankingu). Żeby je zmergować: manually na WP (redirect 301 → ponowić repair).`);
    lines.push(``);
    for (const s of skipped) {
      lines.push(`- [${s?.rule ?? "?"}] \`${s?.slug ?? "?"}\` — ${s?.reason ?? "?"}`);
    }
    lines.push(``);
  }

  if (r6Proposals.length > 0) {
    lines.push(`## 📋 R6 Review Queue — proposed links (${r6Proposals.length})`);
    lines.push(``);
    lines.push(`| Parent (from) | Orphan (to) | Type | Confidence |`);
    lines.push(`|---|---|---|---|`);
    for (const p of r6Proposals) {
      lines.push(`| \`${p.from_node_slug}\` | \`${p.to_node_slug}\` | ${p.relation_type} | ${(p.confidence * 100).toFixed(0)}% |`);
    }
    lines.push(``);
  }

  if (r5Flags.length > 0) {
    lines.push(`## ⚠️ R5 Flagged slugs — info only (${r5Flags.length})`);
    lines.push(``);
    for (const f of r5Flags) {
      lines.push(`- \`${f?.slug ?? "?"}\` — ${(f?.reasons ?? []).join(", ")}`);
    }
    lines.push(``);
  }

  if (logs.length > 0) {
    lines.push(`## Raw log (${logs.length} wpisów)`);
    lines.push(``);
    lines.push("```");
    for (const log of logs) {
      lines.push(`[${log.type.toUpperCase()}] ${log.msg}`);
    }
    lines.push("```");
    lines.push(``);
  }

  return lines.join("\n");
}

/**
 * Plain text log — każdy krok na osobnej linii, monospace-friendly.
 */
function formatReportAsTxt(
  report: RepairReport,
  logs: Array<{ type: string; msg: string }>,
): string {
  const lines: string[] = [];
  const sep = "=".repeat(80);
  const subsep = "-".repeat(80);
  const autoApplied = report.auto_applied ?? ({} as RepairReport["auto_applied"]);
  const r1 = autoApplied.R1_same_seed_merges ?? [];
  const r2 = autoApplied.R2_cross_seed_merges ?? [];
  const r4 = autoApplied.R4_relations_cleaned;
  const r7 = autoApplied.R7_seeds_demoted ?? [];
  const delta = report.re_audit_delta ?? ({} as RepairReport["re_audit_delta"]);
  const skipped = report.skipped?.has_wp_post ?? [];

  lines.push(sep);
  lines.push("MAP REPAIR REPORT");
  lines.push(sep);
  lines.push(`Map ID:     ${report.map_id}`);
  lines.push(`Executed:   ${report.executed_at ?? "?"}`);
  lines.push(`Duration:   ${((report.duration_ms ?? 0) / 1000).toFixed(1)}s`);
  lines.push(`Dry-run:    ${report.dry_run ? "YES" : "NO (APPLIED TO DB)"}`);
  lines.push(``);

  lines.push(subsep);
  lines.push("DELTA METRICS (before → after)");
  lines.push(subsep);
  const scoreBefore = delta.score_before ?? 0;
  const scoreAfter = delta.score_after ?? 0;
  const scoreDelta = scoreAfter - scoreBefore;
  lines.push(`  Total score:       ${scoreBefore} → ${scoreAfter} (${scoreDelta >= 0 ? "+" : ""}${scoreDelta})`);
  lines.push(`  L2 deterministic:  ${delta.l2_det_before ?? "?"} → ${delta.l2_det_after ?? "?"}`);
  lines.push(`  Cross-seed bridges:${delta.cross_seed_bridges_before ?? "?"} → ${delta.cross_seed_bridges_after ?? "?"} (Δ ${delta.cross_seed_bridges_delta ?? "?"})`);
  lines.push(`  Duplikaty rozwiązane: ${delta.duplicates_resolved ?? 0}`);
  lines.push(`  Orphans resolved:  ${delta.orphans_resolved ?? 0}`);
  lines.push(``);

  lines.push(subsep);
  lines.push("SUMMARY");
  lines.push(subsep);
  lines.push(`  R1 same-seed merges:          ${r1.length}`);
  lines.push(`  R2 cross-seed merges:         ${r2.length}`);
  lines.push(`  R4 broken relations cleaned:  ${r4?.deleted_count ?? 0}`);
  lines.push(`  R7 ghost seeds demoted:       ${r7.length}`);
  lines.push(`  SKIPPED (live URL protected): ${skipped.length}`);
  lines.push(``);

  if (r1.length + r2.length > 0) {
    lines.push(subsep);
    lines.push("CHANGES — what replaced what");
    lines.push(subsep);
    let n = 1;
    for (const m of r1) {
      const deleted = m?.deleted ?? [];
      lines.push(`[${n}] R1 [${m?.entity ?? "?"} × ${m?.intent ?? "?"}]`);
      lines.push(`     KEEPS:  ${m?.canonical?.slug ?? "?"}`);
      for (const d of deleted) lines.push(`     DELETE: ${d?.slug ?? "?"}`);
      lines.push(`     rewired ${m?.relations_rewired ?? 0} relations, removed ${m?.self_loops_removed ?? 0} self-loops`);
      lines.push(``);
      n++;
    }
    for (const m of r2) {
      const deleted = m?.deleted ?? [];
      lines.push(`[${n}] R2 [${m?.entity ?? "?"} × ${m?.intent ?? "?"}] target seed: ${(m?.target_seed_id ?? "?").slice(0, 8)}`);
      lines.push(`     KEEPS:  ${m?.canonical?.slug ?? "?"}`);
      for (const d of deleted) lines.push(`     DELETE: ${d?.slug ?? "?"}`);
      lines.push(`     rewired ${m?.relations_rewired ?? 0} relations, removed ${m?.self_loops_removed ?? 0} self-loops`);
      lines.push(``);
      n++;
    }
  }

  if (r7.length > 0) {
    lines.push(subsep);
    lines.push(`R7 GHOST SEEDS DEMOTED (${r7.length})`);
    lines.push(subsep);
    for (const s of r7) {
      lines.push(`  - ${s?.seed_name ?? "?"} (${s?.total_nodes ?? 0} nodów) : ${s?.old_status ?? "?"} → pending_production`);
    }
    lines.push(``);
  }

  if (skipped.length > 0) {
    lines.push(subsep);
    lines.push(`SKIPPED — protected live URLs (${skipped.length})`);
    lines.push(subsep);
    lines.push(`(engine nigdy nie usuwa nodów z matched_wp_post_id)`);
    lines.push(``);
    for (const s of skipped) {
      lines.push(`  [${s?.rule ?? "?"}] ${s?.slug ?? "?"} — ${s?.reason ?? "?"}`);
    }
    lines.push(``);
  }

  if (logs.length > 0) {
    lines.push(subsep);
    lines.push(`RAW LOG (${logs.length} entries)`);
    lines.push(subsep);
    for (const log of logs) {
      lines.push(`[${log.type.toUpperCase().padEnd(7)}] ${log.msg}`);
    }
    lines.push(``);
  }

  lines.push(sep);
  lines.push("END OF REPORT");
  lines.push(sep);

  return lines.join("\n");
}

function humanizeR5Reason(reason: string): string {
  if (reason.startsWith("short_middle_token:")) {
    const token = reason.split(":")[1];
    return `Krótkie słowo "${token}" w środku slugu (nie stopword) — możliwe obcięcie przez LLM`;
  }
  if (reason.startsWith("repeated_token:")) {
    const token = reason.split(":")[1];
    return `Słowo "${token}" powtórzone pod rząd — artefakt generowania`;
  }
  if (reason.startsWith("too_long_")) {
    return `Slug za długi — LLM prawdopodobnie przepisało pół tytułu`;
  }
  if (reason === "double_hyphen") return "Podwójny myślnik (foo--bar) — artefakt normalizacji";
  if (reason === "non_ascii") return "Slug nie został slugifikowany — polskie znaki nadal obecne";
  return reason;
}

function R6Table({
  proposals,
  accepted,
  onToggle,
}: {
  proposals: RelationProposal[];
  accepted: Set<string>;
  onToggle: (key: string, v: boolean) => void;
}) {
  return (
    <div className="rounded-md border bg-card divide-y max-h-[400px] overflow-y-auto">
      <div className="grid grid-cols-[32px,2fr,2fr,80px,100px] gap-2 px-3 py-2 bg-muted/30 text-xs uppercase font-semibold text-muted-foreground">
        <div />
        <div>Parent (from)</div>
        <div>Orphan (to)</div>
        <div>Type</div>
        <div className="text-right">Confidence</div>
      </div>
      {proposals.map((p) => {
        const key = `${p.from_node_id}::${p.to_node_id}`;
        const checked = accepted.has(key);
        return (
          <div
            key={key}
            className="grid grid-cols-[32px,2fr,2fr,80px,100px] gap-2 px-3 py-2 text-sm items-center hover:bg-muted/20"
          >
            <Checkbox checked={checked} onCheckedChange={(v) => onToggle(key, !!v)} />
            <code className="text-xs font-mono break-all">{p.from_node_slug}</code>
            <code className="text-xs font-mono break-all">{p.to_node_slug}</code>
            <Badge variant="outline" className="text-[10px]">
              {p.relation_type}
            </Badge>
            <span className="text-right text-xs tabular-nums font-semibold">
              {(p.confidence * 100).toFixed(0)}%
            </span>
          </div>
        );
      })}
      {proposals.length === 0 && (
        <div className="p-4 text-center text-sm text-muted-foreground">Brak propozycji</div>
      )}
    </div>
  );
}
